import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, Loader2, ShieldCheck } from "lucide-react";
import PageHero from "@/components/PageHero";
import { COMPANY, SERVICES, LOCALITIES, IMG } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createLead } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", area: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Please enter your name and phone.");
    setLoading(true);
    try {
      await createLead({ ...form, source: "contact" });
      toast.success("Message sent! We'll get back to you shortly.");
      setForm({ name: "", phone: "", email: "", service: "", area: "", message: "" });
    } catch { toast.error("Something went wrong. Please call or WhatsApp us."); } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        title="Get in Touch"
        subtitle="Book a free inspection, request a quote, or ask us anything. Our team responds fast — 24/7."
        image={IMG.commercial}
        crumbs={[{ label: "Contact" }]}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: Phone, label: "Call Us", value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}`, color: "text-royal" },
              { icon: MessageCircle, label: "WhatsApp", value: COMPANY.phone, href: `https://wa.me/${COMPANY.phoneRaw}`, color: "text-[#25D366]" },
              { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}`, color: "text-royal" },
              { icon: MapPin, label: "Location", value: COMPANY.city, color: "text-royal" },
              { icon: Clock, label: "Working Hours", value: "24/7 Emergency Support", color: "text-royal" },
            ].map((c) => (
              <a key={c.label} href={c.href} className="flex items-center gap-4 bg-soft-grey rounded-lg p-5 border border-slate-200 hover:border-royal/40 transition-colors">
                <div className="h-12 w-12 rounded-md bg-white flex items-center justify-center shadow-sm"><c.icon className={`h-6 w-6 ${c.color}`} /></div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">{c.label}</div>
                  <div className="font-semibold text-navy-deep">{c.value}</div>
                </div>
              </a>
            ))}
            <div className="bg-navy-deep rounded-lg p-6 text-white flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-gold shrink-0" />
              <p className="text-sm">Free inspection & transparent quote — no obligation, no hidden charges.</p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <form onSubmit={submit} className="bg-soft-grey rounded-lg p-8 border border-slate-200" data-testid="contact-form">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep mb-6">Send Us a Message</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Name*</Label><Input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your name" /></div>
                <div className="space-y-1.5"><Label>Phone*</Label><Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." /></div>
                <div className="space-y-1.5"><Label>Email</Label><Input data-testid="contact-email" value={form.email} onChange={set("email")} placeholder="you@email.com" /></div>
                <div className="space-y-1.5"><Label>Service</Label>
                  <Select value={form.service} onValueChange={set("service")}>
                    <SelectTrigger data-testid="contact-service"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="max-h-60">{SERVICES.map((s) => <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:col-span-2"><Label>Area / Locality</Label>
                  <Select value={form.area} onValueChange={set("area")}>
                    <SelectTrigger data-testid="contact-area"><SelectValue placeholder="Select your area" /></SelectTrigger>
                    <SelectContent className="max-h-60">{LOCALITIES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:col-span-2"><Label>Message</Label><Textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="Tell us about your waterproofing needs" rows={4} /></div>
              </div>
              <button disabled={loading} className="btn-ripple w-full sm:w-auto mt-6 bg-royal hover:bg-royal/90 text-white font-semibold px-8 py-3.5 rounded-sm transition-colors flex items-center justify-center gap-2" data-testid="contact-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 md:px-8 mt-12">
          <div className="rounded-lg overflow-hidden border border-slate-200 h-80">
            <iframe
              title="JAL RAKSHAKK Jaipur Location"
              src="https://www.google.com/maps?q=Jaipur,Rajasthan&output=embed"
              className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
